import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Block,
  DeleteForever,
  ManageSearch,
  MoreHoriz,
  ReceiptLong,
  Refresh,
  Search,
  Upload,
  Visibility,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CleanningButton from '../../components/CleanningButton';
import CustomSwitch from '../../components/CustomSwitch';
import DatePick from '../../components/DatePick';
import MainText from '../../components/MainText';
import { SettlementsAPI } from '../../services/api';
import { addTab, mergeFlow } from '../../tabs/tabsSlice';
import PaymentMethodChip from './components/PaymentMethodChip';
import PaymentStatusChip from './components/PaymentStatusChip';
import {
  formatCurrency,
  formatDate,
  getMethodSummaryLabel,
  normalizeMethodType,
  normalizePaymentsList,
} from './paymentNormalizer';

const METHOD_FILTERS = [
  { value: 'todas', label: 'Todas' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'ach', label: 'Transferencia ACH' },
  { value: 'check', label: 'Cheque' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'other', label: 'Otro' },
];

const CellBox = ({ children, justify = 'flex-start' }) => (
  <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: justify, minWidth: 0 }}>
    {children}
  </Box>
);

function RelatedDocumentsCell({ documents = [] }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const list = Array.isArray(documents) ? documents : [];
  const visible = list.slice(0, 2);
  const rest = list.length - visible.length;

  if (list.length === 0) {
    return (
      <CellBox>
        <Typography variant="body2" color="text.secondary">
          --
        </Typography>
      </CellBox>
    );
  }

  return (
    <CellBox>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, maxWidth: '100%' }}>
        {visible.map((doc) => (
          <Chip
            key={doc.id || doc.code}
            size="small"
            icon={<ReceiptLong fontSize="small" />}
            label={doc.code || doc.number || 'Documento'}
            variant="outlined"
            sx={{ maxWidth: 150, fontWeight: 500 }}
          />
        ))}
        {rest > 0 && (
          <Typography
            variant="body2"
            color="primary"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}
          >
            +{rest}
          </Typography>
        )}
      </Stack>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 360, maxHeight: 360, overflowY: 'auto' } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Documentos relacionados
        </Typography>
        <Stack spacing={0}>
          {list.map((doc, index) => (
            <Box key={doc.id || `${doc.code}-${index}`}>
              <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                  {doc.code || doc.number || 'Documento'}
                </Typography>
                {doc.amount || doc.total ? (
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(doc.amount || doc.total)}
                  </Typography>
                ) : null}
              </Stack>
              {index < list.length - 1 && <Divider />}
            </Box>
          ))}
        </Stack>
      </Popover>
    </CellBox>
  );
}

function MethodsCell({ payment }) {
  const summary = payment?.methodSummary || [];
  const isMixed = summary.length > 1;

  return (
    <CellBox>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
        {isMixed && <PaymentMethodChip mixed />}
        {summary.map((item) => (
          <PaymentMethodChip key={`${item.type}-${item.label}`} summary={item} />
        ))}
      </Stack>
    </CellBox>
  );
}

function AttachmentsCell({ payment }) {
  const count = Number(payment?.attachmentsCount || 0);
  const hasOnlyCash = (payment?.methods || []).every((method) => method.type === 'cash');
  const label = count > 0
    ? `${count} archivo${count === 1 ? '' : 's'}`
    : hasOnlyCash
      ? 'No aplica'
      : 'Sin comprobantes';

  return (
    <CellBox>
      <Chip
        size="small"
        label={label}
        color={count > 0 ? 'success' : hasOnlyCash ? 'default' : 'warning'}
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    </CellBox>
  );
}

function ReceiptCell({ payment }) {
  return (
    <CellBox>
      <PaymentStatusChip value={payment?.receipt?.status || 'pending'} />
    </CellBox>
  );
}

export default function PaymentsList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentsFlow = useSelector((state) => state.tabs?.flows?.payments || {});

  const [rawRows, setRawRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);
  const [gridRefreshKey, setGridRefreshKey] = useState(0);

  const [filters, setFilters] = useState({
    method: 'todas',
    has_receipt: 'todas',
    date_from: null,
    date_to: null,
    search: '',
    active: true,
  });

  useEffect(() => {
    dispatch(addTab({ label: 'Pagos realizados', path: '/payments' }));
  }, [dispatch]);

  useEffect(() => {
    const saved = paymentsFlow?.filters;
    if (saved && typeof saved === 'object') {
      setFilters((prev) => ({ ...prev, ...saved }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(mergeFlow({ name: 'payments', data: { filters } }));
  }, [filters, dispatch]);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name !== 'search') setSelectionModel([]);
  };

  const clearAll = () => {
    setSelectionModel([]);
    setFilters({
      method: 'todas',
      has_receipt: 'todas',
      date_from: null,
      date_to: null,
      search: '',
      active: true,
    });
  };

  const toISO = (value) => {
    if (!value) return undefined;
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      if (Number.isNaN(date.getTime())) return undefined;
      return date.toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        date_from: filters.date_from ? toISO(filters.date_from) : undefined,
        date_to: filters.date_to ? toISO(filters.date_to) : undefined,
      };

      if (!params.date_from && !params.date_to) {
        const today = new Date().toISOString().split('T')[0];
        params.date_from = today;
        params.date_to = today;
      }

      params.active = filters.active === true ? 'true' : 'false';

      const { data } = await SettlementsAPI.paymentsList(params);
      setRawRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRawRows([]);
      setError(err?.response?.data?.detail || 'No se pudieron cargar los pagos.');
    } finally {
      setIsLoading(false);
    }
  }, [filters.date_from, filters.date_to, filters.active]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const consolidatedRows = useMemo(() => normalizePaymentsList(rawRows), [rawRows]);

  const filteredRows = useMemo(() => {
    const method = filters.method;
    const receiptFilter = filters.has_receipt;
    const query = String(debouncedSearch || '').trim().toLowerCase();

    return consolidatedRows.filter((payment) => {
      if (method !== 'todas') {
        const hasMethod = (payment.methods || []).some((item) => item.type === method || normalizeMethodType(item.rawType) === method);
        if (!hasMethod) return false;
      }

      if (receiptFilter !== 'todas') {
        const hasReceipt = ['attached', 'incomplete'].includes(payment?.receipt?.status);
        if (receiptFilter === 'si' && !hasReceipt) return false;
        if (receiptFilter === 'no' && hasReceipt) return false;
      }

      if (!query) return true;

      const haystack = [
        payment.code,
        payment.provider?.name,
        formatCurrency(payment.totalAmount),
        payment.paymentDate,
        payment.status?.label,
        payment.receipt?.status,
        ...(payment.relatedDocuments || []).flatMap((doc) => [doc.code, doc.number]),
        ...(payment.methods || []).flatMap((item) => [item.label, item.rawType, item.reference, item.bank?.label]),
        ...(payment.methodSummary || []).map(getMethodSummaryLabel),
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }, [consolidatedRows, debouncedSearch, filters.method, filters.has_receipt]);

  const selectedPayment = useMemo(
    () => filteredRows.find((row) => row.id === selectionModel[0]),
    [filteredRows, selectionModel]
  );

  const openDetail = useCallback((payment) => {
    if (!payment) return;
    const ids = payment.paymentIds?.length ? payment.paymentIds : [payment.raw?.[0]?.id || payment.id].filter(Boolean);
    const primaryId = ids[0];
    if (!primaryId) return;
    navigate(`/payments/${primaryId}?ids=${ids.join(',')}`, {
      state: { payment, paymentIds: ids },
    });
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    setSelectionModel([]);
    setGridRefreshKey((prev) => prev + 1);
    fetchData();
  }, [fetchData]);

  const runForSelectedPaymentIds = async (action, confirmMessage) => {
    if (!selectedPayment) return;
    const ids = selectedPayment.paymentIds || [];
    if (ids.length === 0) return;
    if (!window.confirm(confirmMessage)) return;
    await Promise.all(ids.map((paymentId) => action(paymentId)));
    setSelectionModel([]);
    await fetchData();
  };

  const columns = useMemo(() => ([
    {
      field: 'paymentDate',
      headerName: 'Fecha',
      width: 130,
      renderCell: ({ row }) => (
        <CellBox>
          <Stack spacing={0.25} alignItems="flex-start">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(row.paymentDate)}
            </Typography>
            {row.dateEstimated ? (
              <Chip size="small" color="warning" variant="outlined" label="Estimada" sx={{ height: 18, fontSize: 10 }} />
            ) : null}
          </Stack>
        </CellBox>
      ),
    },
    {
      field: 'code',
      headerName: 'Codigo',
      width: 130,
      renderCell: ({ row }) => (
        <CellBox>
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Roboto Mono", monospace', fontSize: '0.78rem' }}>
            {row.code}
          </Typography>
        </CellBox>
      ),
    },
    {
      field: 'provider',
      headerName: 'Proveedor',
      flex: 1,
      minWidth: 190,
      renderCell: ({ row }) => (
        <CellBox>
          <Typography variant="body2" sx={{ fontWeight: 400 }} noWrap>
            {row.provider?.name || 'Sin proveedor'}
          </Typography>
        </CellBox>
      ),
    },
    {
      field: 'relatedDocuments',
      headerName: 'Documento',
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => <RelatedDocumentsCell documents={row.relatedDocuments} />,
    },
    {
      field: 'totalAmount',
      headerName: 'Total pagado',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => (
        <CellBox justify="flex-end">
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(row.totalAmount)}
          </Typography>
        </CellBox>
      ),
    },
    {
      field: 'methods',
      headerName: 'Metodos',
      width: 290,
      sortable: false,
      renderCell: ({ row }) => <MethodsCell payment={row} />,
    },
    {
      field: 'attachmentsCount',
      headerName: 'Comprobantes',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => <AttachmentsCell payment={row} />,
    },
    {
      field: 'receipt',
      headerName: 'Recibo proveedor',
      width: 170,
      sortable: false,
      renderCell: ({ row }) => <ReceiptCell payment={row} />,
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 130,
      renderCell: ({ row }) => (
        <CellBox>
          <PaymentStatusChip status={row.status} />
        </CellBox>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 210,
      sortable: false,
      renderCell: ({ row }) => (
        <CellBox>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => openDetail(row)}
              sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
            >
              Ver detalle
            </Button>
            {row.receipt?.status === 'pending' && (
              <Tooltip title="Adjuntar recibo">
                <IconButton size="small" color="primary" onClick={() => openDetail(row)}>
                  <Upload fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Mas acciones">
              <IconButton size="small" onClick={() => openDetail(row)}>
                <MoreHoriz fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CellBox>
      ),
    },
  ]), [openDetail]);




  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <MainText
              title="Pagos realizados"
              subtitle="Historial consolidado de pagos y transacciones"
              icon={ManageSearch}
              width="auto"
            />
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {filters.method !== 'todas' && (
              <Chip
                size="small"
                label={`Metodo: ${METHOD_FILTERS.find((item) => item.value === filters.method)?.label || filters.method}`}
                onDelete={() => handleFilterChange('method', 'todas')}
              />
            )}
            {filters.has_receipt !== 'todas' && (
              <Chip
                size="small"
                label={`Recibo: ${filters.has_receipt === 'si' ? 'Si' : 'No'}`}
                onDelete={() => handleFilterChange('has_receipt', 'todas')}
              />
            )}
            {filters.search && (
              <Chip
                size="small"
                label={`Busqueda: "${filters.search}"`}
                onDelete={() => handleFilterChange('search', '')}
              />
            )}
            {filters.date_from && (
              <Chip
                size="small"
                label={`Desde: ${formatDate(filters.date_from)}`}
                onDelete={() => handleFilterChange('date_from', null)}
              />
            )}
            {filters.date_to && (
              <Chip
                size="small"
                label={`Hasta: ${formatDate(filters.date_to)}`}
                onDelete={() => handleFilterChange('date_to', null)}
              />
            )}
            {filters.active === false && (
              <Chip
                size="small"
                label="Mostrando pagos anulados"
                color="warning"
                onDelete={() => handleFilterChange('active', true)}
              />
            )}
            {!filters.date_from && !filters.date_to && filters.active === true && (
              <Chip size="small" label="Mostrando solo hoy" color="info" />
            )}
          </Stack>

          <CleanningButton onClick={clearAll} />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <DatePick
            label="Fecha inicio"
            size="small"
            value={filters.date_from}
            onChange={(date) => handleFilterChange('date_from', date)}
            allowHistoricalDates
            allowFutureDates={false}
            maxWidth={200}
            returnType="date"
          />
          <DatePick
            label="Fecha fin"
            size="small"
            value={filters.date_to}
            onChange={(date) => handleFilterChange('date_to', date)}
            allowHistoricalDates
            allowFutureDates={false}
            maxWidth={200}
            returnType="date"
          />
          <FormControl sx={{ minWidth: 170 }} size="small">
            <InputLabel>Metodo</InputLabel>
            <Select
              label="Metodo"
              value={filters.method}
              onChange={(event) => handleFilterChange('method', event.target.value)}
            >
              {METHOD_FILTERS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Recibo</InputLabel>
            <Select
              label="Recibo"
              value={filters.has_receipt}
              onChange={(event) => handleFilterChange('has_receipt', event.target.value)}
            >
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="si">Si</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            size="small"
            sx={{ width: { xs: '100%', sm: 260 } }}
            placeholder="Buscar pago, proveedor, factura..."
            value={filters.search}
            onChange={(event) => handleFilterChange('search', event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <CustomSwitch
            name="active"
            label={filters.active ? 'Activos' : 'Inactivos'}
            initialValue={filters.active}
            onChange={(name, value) => handleFilterChange(name, value)}
          />

          <Tooltip title="Refrescar">
            <IconButton size="small" onClick={handleRefresh}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {filters.active ? (
              <Tooltip title="Anular pago completo">
                <span>
                  <IconButton
                    color="warning"
                    size="small"
                    disabled={!selectedPayment || selectedPayment.status?.value === 'cancelled'}
                    onClick={() => runForSelectedPaymentIds(
                      SettlementsAPI.cancelPayment,
                      'Seguro que deseas anular este pago completo?'
                    )}
                  >
                    <Block fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Eliminar pago definitivamente">
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    disabled={!selectedPayment || selectedPayment.status?.value !== 'cancelled'}
                    onClick={() => runForSelectedPaymentIds(
                      SettlementsAPI.destroyPayment,
                      'Eliminar definitivamente este pago anulado? Esta accion no se puede deshacer.'
                    )}
                  >
                    <DeleteForever fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ height: 520, width: '100%' }}>
          <DataGrid
            key={gridRefreshKey}
            rows={filteredRows}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[5, 10, 20, 50]}
            pagination
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => {
              const next = Array.isArray(model) ? model.slice(0, 1) : [];
              setSelectionModel(next);
            }}
            disableColumnFilter
            disableColumnMenu
            rowHeight={54}
            columnHeaderHeight={50}
            localeText={{ noRowsLabel: 'No hay pagos registrados' }}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#10173c',
                color: '#ffffff',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#10173c !important',
                '&:hover, &:focus, &:active': {
                  backgroundColor: '#10173c !important',
                },
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#ffffff',
                fontWeight: 600,
              },
              '& .MuiDataGrid-iconButtonContainer': {
                color: '#ffffff !important',
              },
              '& .MuiDataGrid-menuIcon': {
                color: '#ffffff !important',
              },
              '& .MuiDataGrid-columnSeparator': {
                color: '#ffffff !important',
                '&:hover': { color: '#ffffff !important' },
              },
              '& .MuiDataGrid-cell': {
                px: 1,
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'rgba(16, 23, 60, 0.08)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(16, 23, 60, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 23, 60, 0.15)',
                  },
                },
              },
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
}
